import { FC, useMemo, useCallback } from 'react'
import styles from 'components/features/coaching/advisor/AdvisorCard/AdvisorCard.styl'
import { AdvisorRequestPanePreset } from 'components/features/coaching/advisorMeeting/AdvisorRequestPanePreset/AdvisorRequestPanePreset'
import { AdvisorUid } from 'components/features/coaching/advisor/types/advisor'
import { useStoreSelector, useStoreDispatch } from 'helpers/reactReduxHelpers'
import { advisorsSearchResultsSelector, advisorsVisibleSelector } from 'components/features/coaching/advisorMeeting/selectors/advisorsSelector'
import { AdvisorCard } from 'components/features/coaching/advisor/AdvisorCard/AdvisorCard'
import { modifyFloatingPaneActionCreator } from 'actions/floatingPanesActions'
import { PageAnchorHandler } from 'handlers/PageAnchorHandler/PageAnchorHandler'
import { MeetingPaneError } from 'components/features/coaching/advisorMeeting/MeetingPaneError/MeetingPaneError'
import { CardBlock } from 'components/features/pfm/Blocks/Blocks'
import { setVisibleAdvisorsActionCreator } from 'components/features/coaching/advisorStore/actions/advisorMeetingActions'
import { TopicStepOutputFields } from 'components/features/coaching/advisorMeeting/types/floatPane'

type AdvisorRequestScheduleProps = {
  fields: TopicStepOutputFields
  onClose: () => void
}

export const AdvisorRequestSchedule: FC<AdvisorRequestScheduleProps> = (props) => {
  const { onClose, fields } = props

  const advisors = useStoreSelector(advisorsSearchResultsSelector)
  const advisorsVisible = useStoreSelector(advisorsVisibleSelector)
  const dispatch = useStoreDispatch()

  const handleSeeMoreAdvisors = useCallback(() => {
    dispatch(setVisibleAdvisorsActionCreator(advisorsVisible + 1))
  }, [dispatch, advisorsVisible])

  const handleTimeslot = useCallback((advisor_uid: AdvisorUid, timeslot: string) => {
    dispatch(modifyFloatingPaneActionCreator(
      'advisor', {
        mode: 'request',
        step: 'confirm',
        fields: {
          ...fields,
          advisor_uid,
          timeslot,
        },
      }
    ))
  }, [dispatch, fields])

  const seeAnotherAdvisorChild = useMemo(() => advisors && advisorsVisible < advisors.length && (
    <button className={ styles.moreAdvisorsLink } onClick={ handleSeeMoreAdvisors }>
      See Another Coach
    </button>
  ), [advisors, advisorsVisible, handleSeeMoreAdvisors])

  const emptyStateChild = useMemo(() => (
    <CardBlock className={ styles.emptyState } forceRoundedCorners shadowTheme='medium'>
      <h3>No coaches available at the moment</h3>
      <h6>Please check back at a later time</h6>
    </CardBlock>
  ), [])

  const advisorsFound = !!advisors && advisors.length > 0

  const advisorsChild = useMemo(() => !advisorsFound ? emptyStateChild : advisors?.slice(0, advisorsVisible).map((uid) => {
    return (
      <PageAnchorHandler key={ uid } placement={{ anchor: 'advisor_timeslot', id: uid }}>
        <CardBlock className={ styles.advisorCardWrapper } forceRoundedCorners shadowTheme='medium'>
          <AdvisorCard uid={ uid } onTimeslot={ (timeslot) => handleTimeslot(uid, timeslot) } />
        </CardBlock>
      </PageAnchorHandler>
    )
  }), [advisors, advisorsVisible, handleTimeslot, emptyStateChild, advisorsFound])

  const errorChild = useMemo(() => (
    <MeetingPaneError />
  ), [])

  return (
    <AdvisorRequestPanePreset
      title='Schedule a New Coaching Session'
      subTitle='2. Pick a time'
      notification={ errorChild }
      onCancel={ onClose }
      progressBarPercent={ 60 }
    >
      { advisorsChild }
      { seeAnotherAdvisorChild }
    </AdvisorRequestPanePreset>
  )
}
